import { fetchAuthSession, fetchUserAttributes } from "aws-amplify/auth"
import { useCallback, useRef } from "react"

interface WebSocketPaymentOptions {
  paymentIntentId: string
  onSuccess?: (data: PaymentStatusUpdate) => void
  onFailure?: (data: PaymentStatusUpdate) => void
  onWebSocketClosed?: () => void
}

interface PaymentStatusUpdate {
  type: string
  eventType?: string
  paymentIntentId?: string
  status?: string
  amount?: number
  currency?: string
  failureCode?: string
  failureMessage?: string
  cancellationReason?: string
  timestamp?: string
}

// Récupérer l'URL WebSocket depuis l'environnement
const getWebSocketUrl = () => {
  // Priorité à une URL complète fournie par l'env
  const envUrl = import.meta.env?.VITE_WS_URL as string | undefined
  console.log("[WebSocket] Env URL:", envUrl)
  if (envUrl && envUrl.startsWith("ws")) return envUrl.replace(/\/+$/, "")

  // Construction depuis API ID/Region/Stage si fourni
  const apiId = import.meta.env?.VITE_WS_API_ID as string | undefined
  const region =
    (import.meta.env?.VITE_AWS_REGION as string | undefined) || "eu-west-3"
  const stage = (import.meta.env?.VITE_ENV as string | undefined) || "test"
  if (apiId)
    return `wss://${apiId}.execute-api.${region}.amazonaws.com/${stage}`

  // Fallback legacy (à remplacer via env)
  return "wss://fndvbkg8aj.execute-api.eu-west-3.amazonaws.com/test"
}

export const useWebSocketPayment = () => {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Fonction pour se connecter au WebSocket
  const connect = useCallback(
    async ({
      paymentIntentId,
      onSuccess,
      onFailure,
    }: WebSocketPaymentOptions) => {
      console.log("[WebSocket] Connecting to WebSocket...")
      console.log("[WebSocket] Payment intent ID:", paymentIntentId)
      if (!paymentIntentId) return

      try {
        // Récupère un access token pour l'authorizer WebSocket (tokenUse: "access")
        const session = await fetchAuthSession({ forceRefresh: true })
        const token = session.tokens?.accessToken?.toString()
        if (!token) {
          console.error("[WebSocket] No authentication token available")
          return
        }

        // Remove "Bearer " prefix if present
        const cleanToken = token.startsWith("Bearer ")
          ? token.substring(7)
          : token

        // Récupérer le shopId depuis les attributs utilisateur
        const userAttributes = await fetchUserAttributes()
        const shopId = userAttributes["custom:shopId"] || ""

        if (!shopId) {
          console.error("[WebSocket] No shopId found in user attributes")
          return
        }

        const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(
          cleanToken
        )}&paymentIntentId=${encodeURIComponent(
          paymentIntentId
        )}&shopId=${encodeURIComponent(shopId)}`

        const ws = new WebSocket(wsUrl)
        console.log("[WebSocket] WebSocket instance created:", {
          url: ws.url,
          readyState: ws.readyState,
          protocol: ws.protocol,
        })
        wsRef.current = ws

        ws.onopen = () => {
          console.log("[WebSocket] Connected successfully")

          // Stop polling if it was running
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
          }

          // Start heartbeat
          heartbeatIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ action: "ping" }))
            }
          }, 30000) // Ping every 30 seconds
        }

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data) as PaymentStatusUpdate
            console.log("[WebSocket] Message received:", data)

            // Handle different message types
            if (data.type === "pong") {
              // Heartbeat response, ignore
              return
            }

            if (data.type === "error") {
              console.error("[WebSocket] Server error:", data)
              const errorMessage =
                "message" in data && typeof data.message === "string"
                  ? data.message
                  : "Server error"
              console.error("[WebSocket] Server error:", errorMessage)
              return
            }

            // Payment status update
            if (data.type === "payment_status_update" || data.eventType) {
              // Check for specific events
              if (data.eventType === "payment_intent.succeeded") {
                onSuccess?.(data)
              } else if (data.eventType === "payment_intent.payment_failed") {
                onFailure?.(data)
              }
            }
          } catch (error) {
            console.error("[WebSocket] Failed to parse message:", error)
          }
        }

        ws.onerror = (event) => {
          console.error("[WebSocket] Connection error:", event)
          throw new Error("WebSocket connection error")
        }

        ws.onclose = (event) => {
          console.log("[WebSocket] Connection closed:", {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          })
          console.log("[WebSocket] Connection closed")
          wsRef.current = null

          // Clear heartbeat
          if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current)
            heartbeatIntervalRef.current = null
          }
        }
      } catch (error) {
        console.error("[WebSocket] Failed to connect:", error)
        console.error("[WebSocket] Failed to establish WebSocket connection")
        throw new Error("Failed to establish WebSocket connection")
      }
    },
    []
  )

  // Fonction pour fermer la connexion
  const disconnect = useCallback(() => {
    console.log("[WebSocket] Disconnecting...")

    // Clear all timers
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }

    // Close WebSocket connection
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close(1000, "Client disconnect")
    }

    wsRef.current = null
    console.log("[WebSocket] Connection closed")
  }, [])

  return {
    connect,
    disconnect,
  }
}
